import { Alert } from "antd";
import styled from "styled-components";

import Icon from "shared/components/Icon";
import { COLORS } from "shared/config/constants";

const CosignConnectCard = styled.div`
  max-width: 480px;
  padding: 32px;
  border: 1px solid ${COLORS.borders};
  border-radius: 8px;
  background-color: ${COLORS.white};
`;

const CosignLogoWrapper = styled.div`
  margin-bottom: 24px;
`;

const CosignUuidLabel = styled.div`
  font-size: 12px;
  color: ${COLORS.textColorSecondary};
  margin-bottom: 4px;
`;

const CosignUuidValue = styled.div`
  font-family: monospace;
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 16px;
`;

const CosignSetupInput = styled.input`
  width: 100%;
  margin-bottom: 12px;
  padding: 10px 12px;
  border: 1px solid ${COLORS.borders};
  border-radius: 4px;
  font-size: 14px;

  &:disabled {
    background-color: ${COLORS.background};
    cursor: not-allowed;
  }
`;

const CosignUuidInput = styled(CosignSetupInput)<{ $invalid?: boolean }>`
  font-family: monospace;
  ${({ $invalid }) => $invalid && `border: 2px solid ${COLORS.red};`}
`;

const CosignUuidError = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 12px;
  font-size: 14px;
  color: ${COLORS.red};

  svg {
    flex: 0 0 auto;
    margin-top: 1px;
  }
`;

const CosignButtonSpinner = styled(Icon.LoadingSpinnerIcon)`
  width: 16px;
  height: 16px;
  vertical-align: middle;

  path {
    fill: currentColor;
  }
`;

const CosignErrorIcon = styled(Icon.RedExclamationHollow)`
  width: 18px;
  height: 18px;

  path {
    fill: currentColor;
  }
`;

const CosignConnectAlert = styled(Alert)`
  margin-bottom: 12px;
`;

const CosignHelpText = styled.p`
  margin-bottom: 12px;
  font-size: 13px;
  color: ${COLORS.textColorSecondary};
`;

const CreatePropertyForm = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const DuplicateWarningBanner = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 16px;
  margin-bottom: 16px;
  border: 1px solid ${COLORS.warning};
  border-left-width: 4px;
  border-radius: 6px;
  background-color: #fff6f2;
`;

const DuplicateWarningText = styled.div`
  font-size: 13px;
  color: ${COLORS.textColorSecondary};

  strong {
    color: ${COLORS.warning};
  }
`;

const DuplicateWarningDismiss = styled.button`
  flex-shrink: 0;
  border: none;
  background: none;
  padding: 0;
  cursor: pointer;
  font-size: 13px;
  color: ${COLORS.textColorSecondary};
  text-decoration: underline;
`;

const CreatePropertyError = styled.p`
  margin-bottom: 12px;
  color: ${COLORS.red};
  font-size: 13px;
`;

const PropertySummary = styled.div`
  margin-bottom: 16px;
  padding: 12px 16px;
  border: 1px solid ${COLORS.borders};
  border-radius: 6px;
  background-color: ${COLORS.background};
`;

const PropertySummaryLabel = styled.div`
  margin-bottom: 4px;
  font-size: 12px;
  color: ${COLORS.textColorSecondary};
`;

const PropertySummaryValue = styled.div`
  font-size: 14px;
  font-weight: 600;
`;

const PropertySearchInput = styled.input`
  width: 100%;
  margin-bottom: 16px;
  padding: 10px 12px;
  border: 1px solid ${COLORS.borders};
  border-radius: 4px;
`;

const PropertyOptionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 280px;
  overflow-y: auto;
`;

const PropertyOptionSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const PropertyOptionSectionTitle = styled.div`
  margin-top: 8px;
  font-size: 12px;
  font-weight: 600;
  color: ${COLORS.textColorSecondary};

  &:first-child {
    margin-top: 0;
  }
`;

const PropertyOptionButton = styled.button<{ $selected?: boolean }>`
  width: 100%;
  padding: 12px 16px;
  border: 1px solid
    ${({ $selected }) => ($selected ? COLORS.primary : COLORS.borders)};
  border-radius: 6px;
  background-color: ${({ $selected }) =>
    $selected ? "#f1f7ff" : COLORS.white};
  cursor: pointer;
  text-align: left;
`;

const PropertyOptionName = styled.div`
  font-size: 14px;
  font-weight: 600;
`;

const PropertyOptionMeta = styled.div`
  margin-top: 4px;
  font-size: 12px;
  color: ${COLORS.textColorSecondary};
`;

const EmptyPropertyList = styled.div`
  padding: 20px;
  border: 1px dashed ${COLORS.borders};
  border-radius: 6px;
  color: ${COLORS.textColorSecondary};
  text-align: center;
`;

const PropertyRetryButton = styled.button`
  margin-top: 8px;
  padding: 0;
  border: none;
  background: none;
  color: ${COLORS.primary};
  cursor: pointer;
  text-decoration: underline;
`;

export {
  CosignConnectCard,
  CosignLogoWrapper,
  CosignUuidLabel,
  CosignUuidValue,
  CosignSetupInput,
  CosignUuidInput,
  CosignUuidError,
  CosignButtonSpinner,
  CosignErrorIcon,
  CosignConnectAlert,
  CosignHelpText,
  CreatePropertyForm,
  DuplicateWarningBanner,
  DuplicateWarningText,
  DuplicateWarningDismiss,
  CreatePropertyError,
  PropertySummary,
  PropertySummaryLabel,
  PropertySummaryValue,
  PropertySearchInput,
  PropertyOptionList,
  PropertyOptionSection,
  PropertyOptionSectionTitle,
  PropertyOptionButton,
  PropertyOptionName,
  PropertyOptionMeta,
  EmptyPropertyList,
  PropertyRetryButton,
};
